import { Link } from "react-router-dom";
import { Phone, Heart, Users, MapPin, Award, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const values = [
  {
    icon: Heart,
    title: "Care & Attention",
    description: "We treat every home like our own, paying attention to the details that matter most.",
  },
  {
    icon: Users,
    title: "Community First",
    description: "As a local business, we're invested in serving our West Texas neighbors with pride.",
  },
  {
    icon: Award,
    title: "Quality Guaranteed",
    description: "We stand behind our work. If you're not happy, we'll make it right.",
  },
  {
    icon: MapPin,
    title: "Locally Owned",
    description: "Born and raised in West Texas, we understand the unique needs of our community.",
  },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-sky-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                About West Texas Sparkle
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We're more than just a cleaning company — we're your neighbors, committed to 
                bringing the sparkle back to West Texas homes and businesses.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Founded with a simple mission: provide reliable, affordable, and thorough 
                cleaning services that busy families and business owners can count on.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-sky-100/50 rounded-3xl blur-xl"></div>
              <img
                src="https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="West Texas Sparkle cleaning team"
                className="relative rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-white" data-testid="our-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
            Our Story
          </h2>
          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="leading-relaxed mb-6">
              West Texas Sparkle Cleaning was born from a simple idea: everyone deserves to come 
              home to a clean, fresh space without the stress of doing it themselves. After years 
              of helping friends and family with their cleaning needs, we decided to turn our 
              passion for clean spaces into a business that serves our entire community.
            </p>
            <p className="leading-relaxed mb-6">
              Based right here in Lubbock, we understand the unique challenges that West Texas 
              living brings — from the dust that never seems to quit to the busy schedules that 
              leave little time for deep cleaning. That's why we've built our service around 
              flexibility, reliability, and attention to detail.
            </p>
            <p className="leading-relaxed">
              Today, we proudly serve families and businesses throughout Lubbock, Wolfforth, and 
              Levelland. Every member of our team is background-checked, trained, and committed 
              to treating your home with the same care and respect they'd give their own.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What We Stand For
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our values guide everything we do, from the way we clean to how we treat our customers.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card 
                key={value.title} 
                className="service-card bg-white border-slate-100"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 md:py-24 bg-white" data-testid="service-area">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Proudly Serving West Texas
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            We provide residential and commercial cleaning services throughout:
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {["Lubbock, TX", "Wolfforth, TX", "Levelland, TX"].map((area) => (
              <span 
                key={area}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-50 text-sky-700 font-medium"
              >
                <MapPin className="w-4 h-4" />
                {area}
              </span>
            ))}
          </div>
          <p className="text-slate-500 text-sm">
            Not sure if we service your area? Give us a call!
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-sky-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Let's Get Your Space Sparkling
          </h2>
          <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
            Ready to experience the West Texas Sparkle difference? Contact us today for a 
            free, no-obligation quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8065558123">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-white text-sky-700 hover:bg-sky-50 rounded-full h-14 px-8 text-lg font-semibold shadow-lg"
                data-testid="about-cta-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                (806) 555-8123
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/30 bg-transparent hover:bg-white/10 text-white rounded-full h-14 px-8 text-lg font-semibold"
                data-testid="about-cta-quote"
              >
                Get a Free Quote
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
